import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// --- INLINE NUMBER TO WORDS FALLBACK ---
const numberToWords = (num) => {
  if (!num || isNaN(num)) return "Zero Only";
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + inWords(n % 100) : "");
    if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + inWords(n % 1000) : "");
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + inWords(n % 100000) : "");
    return inWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + inWords(n % 10000000) : "");
  };
  return `${inWords(num)} Rupees Only`;
};

// --- DEFAULT DEMO DATA ---
const defaultProjectData = {
  clientName: "Nexus Global Systems Corp",
  contactPerson: "Alexander Vance",
  officeAddress: "Corporate Tower B, Tech Park Sector 5, Bangalore, KA",
  projectScope: "Enterprise ERP & Supply Chain System Implementation",
  executionPeriod: "2026-2029",
  duration: "4 Years",
  projectId: "PRJ-2026-8902",
};

const ProjectCostEstimateLetter = ({ project = defaultProjectData }) => {
  const date = new Date();
  const navigate = useNavigate();
  
  // Redux authentication fallback
  const auth = useSelector((state) => state?.authentication || {});
  const userData = auth?.userData || { branch: "HQ", token: "demo-token" };

  const [isEditMode, setIsEditMode] = useState(false);
  const [feeStructureData, setFeeStructureData] = useState("");
  const [saveSpinner, setSaveSpinner] = useState(false);
  const [totals, setTotals] = useState({ service: 0, infra: 0 });
  const printRef = useRef(null);

  const [formData, setFormData] = useState({
    clientName: project?.clientName || "",
    contactPerson: project?.contactPerson || "",
    address: project?.officeAddress || project?.address || "",
    projectScope: project?.projectScope || "",
    executionPeriod: project?.executionPeriod || "2026-2027",
    duration: project?.duration || "4 Years",
    projectId: project?.projectId || "",
    
    // Phase Cost Breakdown
    year1Service: "350000",
    year1Infra: "120000",
    year2Service: "300000",
    year2Infra: "100000",
    year3Service: "250000",
    year3Infra: "80000",
    year4Service: "200000",
    year4Infra: "80000",
    totalInWords: "",
  });

  const totalPhases = parseInt(formData.duration) || 4;
  const phases = Array.from({ length: totalPhases }, (_, i) => i + 1);

  const safeNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Recalculate totals dynamically
  useEffect(() => {
    let serviceTotal = 0;
    let infraTotal = 0;

    for (let i = 1; i <= 4; i++) {
      serviceTotal += safeNumber(formData[`year${i}Service`]);
      infraTotal += safeNumber(formData[`year${i}Infra`]);
    }

    setTotals({ service: serviceTotal, infra: infraTotal });
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generateCertificate = async () => {
    setSaveSpinner(true);
    const API_BASE = `${process.env.REACT_APP_COLLEGE_ERP_API || "/"}api/`;

    const url = isEditMode
      ? `${API_BASE}project-cost?id=${feeStructureData._id}&projectId=${formData.projectId}&branch=${userData.branch}`
      : `${API_BASE}project-cost?branch=${userData.branch}`;

    const method = isEditMode ? axios.put : axios.post;
    const payload = {
      executionPeriod: formData.executionPeriod,
      projectScope: formData.projectScope,
      clientName: formData.clientName,
      projectId: formData.projectId,
      costEstimateLetter: formData,
    };

    try {
      await method(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userData.token}`,
        },
      });
      alert("Cost Estimate Letter saved successfully!");
    } catch (err) {
      console.error("Submission error:", err);
      alert("Note: Saved locally. Backend service unreachable.");
    } finally {
      setSaveSpinner(false);
    }
  };

  const generateDurationText = (duration, executionPeriod) => {
    if (!duration || !executionPeriod) return "";
    const startYear = parseInt(executionPeriod.split("-")[0]);
    const durationYears = parseInt(duration);

    if (isNaN(startYear) || isNaN(durationYears)) return duration;
    const endYear = startYear + durationYears;
    return `${durationYears} Years (${startYear}-${endYear})`;
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank");

    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Project Cost Estimate</title>
            <style>
              @page {
                size: A4;
                margin: 100px 50px 50px 50px;
              }
              body {
                font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
                margin: 0;
                color: #111;
              }
              .certificate-container {
                padding: 20px;
                width: 95%;
                margin: auto;
              }
              h2 {
                text-align: center;
                text-decoration: underline;
                margin-bottom: 25px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
              }
              td, th {
                border: 1px solid #333;
                padding: 10px;
                text-align: center;
              }
            </style>
          </head>
          <body onload="window.print(); window.onafterprint = function() { window.close(); }">
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      alert("Pop-ups are blocked! Please allow pop-ups to print.");
    }
  };

  return (
    <Container className="py-4">
      <h3>Project Cost Estimate & Fee Structure Entry</h3>
      <Form>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Client / Organization Name</Label>
              <Input
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Authorized Contact Person</Label>
              <Input
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label>Corporate Address</Label>
          <Input
            type="textarea"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </FormGroup>

        <Row>
          <Col md={4}>
            <FormGroup>
              <Label>Project Scope / Description</Label>
              <Input
                name="projectScope"
                value={formData.projectScope}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Execution Period</Label>
              <Input
                type="select"
                name="executionPeriod"
                value={formData.executionPeriod}
                onChange={handleChange}
              >
                <option>2025-2026</option>
                <option>2026-2027</option>
                <option>2027-2028</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Project Duration</Label>
              <Input
                type="select"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
              >
                <option>1 Year</option>
                <option>2 Years</option>
                <option>3 Years</option>
                <option>4 Years</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <FormGroup style={{ width: "300px" }}>
          <Label>Project / Tracking ID</Label>
          <Input
            name="projectId"
            value={formData.projectId}
            onChange={handleChange}
          />
        </FormGroup>

        <h5 className="mt-4">Phase-wise Cost Breakdown</h5>
        {phases.map((phase) => (
          <Row key={phase}>
            <Col md={6}>
              <FormGroup>
                <Label>Phase {phase} Professional Service Fees (₹)</Label>
                <Input
                  name={`year${phase}Service`}
                  value={formData[`year${phase}Service`] || ""}
                  onChange={handleChange}
                  type="text"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Phase {phase} Infrastructure & Operations (₹)</Label>
                <Input
                  name={`year${phase}Infra`}
                  value={formData[`year${phase}Infra`] || ""}
                  onChange={handleChange}
                  type="text"
                />
              </FormGroup>
            </Col>
          </Row>
        ))}

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Total Professional Fees (Auto Calculated)</Label>
              <Input value={`₹ ${totals.service.toLocaleString("en-IN")}`} readOnly />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Total Infrastructure & Operations (Auto Calculated)</Label>
              <Input value={`₹ ${totals.infra.toLocaleString("en-IN")}`} readOnly />
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label>Grand Total in Words</Label>
          <Input
            name="totalInWords"
            value={numberToWords(totals.service + totals.infra)}
            readOnly
          />
        </FormGroup>

        {saveSpinner ? (
          <Spinner color="primary" />
        ) : (
          <Button color="primary" onClick={generateCertificate}>
            Save & Generate Estimate
          </Button>
        )}
        <Button color="secondary" onClick={handlePrint} className="ms-2">
          Print Estimate Letter
        </Button>
      </Form>

      {/* PRINTABLE PREVIEW DOCUMENT */}
      <div className="mt-5" ref={printRef}>
        <div className="certificate-container">
          <div className="d-flex justify-content-end align-items-center">
            <strong>
              Date:{" "}
              {date.toLocaleString("en-IN", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </strong>
          </div>

          <h2>TO WHOMSOEVER IT MAY CONCERN</h2>

          <p>
            This is to certify and confirm that M/s.{" "}
            <strong>{formData.clientName}</strong>, represented by Mr./Ms.{" "}
            <strong>{formData.contactPerson}</strong>, located at{" "}
            <strong>{formData.address}</strong>, has been formally awarded and registered
            for the <strong>{formData.projectScope}</strong> execution under
            Global Manufacturing & Engineering Operations starting in the fiscal period{" "}
            <strong>{formData.executionPeriod}</strong>.
          </p>

          <p>
            The planned multi-phase timeline for completion is{" "}
            <strong>
              {generateDurationText(formData?.duration, formData?.executionPeriod)}
            </strong>
            . The registered Master Project ID is <strong>{formData.projectId}</strong>.
          </p>

          <p>
            This engagement operates in full compliance with ISO 9001:2015 Quality Standards
            and Corporate Financial Procurement Regulations.
          </p>

          <p>The structured financial milestone and cost schedule is detailed below:</p>

          <table
            style={{
              width: "100%",
              marginTop: "20px",
              textAlign: "center",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #000", padding: "8px" }}>PROJECT PHASE</th>
                <th style={{ border: "1px solid #000", padding: "8px" }}>SERVICE FEES</th>
                <th style={{ border: "1px solid #000", padding: "8px" }}>INFRASTRUCTURE & OPS</th>
              </tr>
            </thead>
            <tbody>
              {phases.map((phase) => (
                <tr key={phase}>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>
                    Phase {["I", "II", "III", "IV"][phase - 1]}
                  </td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>
                    ₹ {Number(formData[`year${phase}Service`] || 0).toLocaleString("en-IN")}
                  </td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>
                    ₹ {Number(formData[`year${phase}Infra`] || 0).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  <strong>SUBTOTALS</strong>
                </td>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  <strong>₹ {totals.service.toLocaleString("en-IN")}</strong>
                </td>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  <strong>₹ {totals.infra.toLocaleString("en-IN")}</strong>
                </td>
              </tr>
              <tr>
                <td
                  colSpan="3"
                  style={{
                    border: "1px solid #000",
                    padding: "10px",
                    textAlign: "left",
                  }}
                >
                  <strong>
                    COMMERCIAL ESTIMATE TOTAL: ₹{" "}
                    {(totals.service + totals.infra).toLocaleString("en-IN")} /- (
                    {numberToWords(totals.service + totals.infra)})
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ marginTop: "40px", textAlign: "right" }}>
            <strong>Authorized Signatory / Finance Director</strong>
          </p>

          <h5 className="mt-4 pt-2">
            Banking & Electronic Funds Transfer (EFT) Information
          </h5>

          <div className="mt-2" style={{ fontSize: "14px", lineHeight: "1.6" }}>
            Beneficiary Name: Global Logistics & Manufacturing Corp
            <br />
            Depository Bank & Branch: FEDERAL BANK, Thanisandra Branch, Bangalore
            <br />
            Account Number: 23880200000311
            <br />
            IFSC / RTGS Code: FDRL0002388
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ProjectCostEstimateLetter;